/**
 * Motor do detalhe: decide o nível (3600, 7200 ou 14400 px), quais blocos estão
 * à vista e o que baixar/decodificar — o mesmo modelo do satélite e do radar:
 *
 * - nível 1: os blocos à vista de TODOS os quadros do loop são baixados, do
 *   quadro atual em diante (até `BYTES_MAX`, pelo tamanho MEDIDO dos blocos;
 *   além disso, um horizonte à frente — o que sai volta do cache HTTP do
 *   navegador, sem rede);
 * - nível 2: só uma JANELA de quadros fica decodificada, pelo orçamento de
 *   pixels (o detalhe reserva do satélite só o que usa, até `FRACAO_MAX`);
 * - a reprodução espera o detalhe do próximo quadro (`registerFrameGate`) e a
 *   exportação, o do quadro atual — nada pisca nem sai borrado no vídeo;
 * - um ANEL de blocos em volta da vista, do quadro na tela, é baixado por
 *   último com o mapa parado: arrastar o mapa não espera a rede;
 * - de perto, com os blocos cobrindo a vista, a base (imagem inteira) desce a
 *   `PARAMETROS.basePerto` — nunca no meio da reprodução;
 * - na transição para a foto de fundo, nenhum nível novo; sem GOES na tela, nenhum
 *   pedido;
 * - conexão econômica (conexao.ts): teto 3600, só a janela adiante, sem anel;
 * - bloco que não existe no nível pedido (404 — de noite não há 14400): o do
 *   nível de baixo, do mesmo quadro, é pedido para o quadro atual e a janela, e
 *   o quadro conta como pronto com ele (`posicaoPronta`, camada.ts desenha).
 *
 * Com o servidor de blocos no ar e qualidade automática, "Alta" ou "Máxima"
 * (regras.ts); senão o satélite segue como sempre, com o arquivo inteiro.
 */
import type OlMap from 'ol/Map';
import type { Extent } from 'ol/extent';
import { detectDeviceProfile, MAX_DECODE_WINDOW } from '../goes/budget';
import type { FrameRequest } from '../goes/frameCache';
import type { ImageWidth } from '../goes/star';
import { subscribeSelector } from '../lib/store';
import { sectorCssPixels } from '../loop/controller';
import { decodeWindow } from '../loop/frames';
import { activePlanLayers, currentPlayWidth, usePlanStore, type LoopPlan } from '../loop/plan';
import { getPlaybackDirection } from '../loop/playback';
import { registerFrameGate, reservePixelBudget } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { goesFade } from '../map/fundoLayer';
import { imageExtentFor } from '../map/imageGeometry';
import { useTimeStore } from '../state/time';
import { useViewStore } from '../state/view';
import {
  blocosDaFaixa,
  blocosDe,
  blocosDoAnel,
  chaveDoBloco,
  faixaComAnel,
  faixaCorrespondente,
  faixaVisivel,
  LARGURA_BASE,
  nivelAbaixo,
  nivelDoProduto,
  nivelPedido,
  pixelsDoBloco,
  urlDoBloco,
  vistaDentroDaImagem,
  type Faixa,
  type Nivel,
} from './blocos';
import { blocoCache } from './cache';
import { acompanharConexao, conexaoEconomica, useConexaoStore } from './conexao';
import { ligarEspelho } from './espelho';
import { PROGRESSO_VAZIO, registrarBaseDeAbertura, useDetalheStore } from './estado';
import { detalheLigado, tetoDoDetalhe } from './regras';
import { niveisDo, useBlocosStore, verificarBlocos } from './saude';
import { bytesPorBloco } from './tamanhos';
import { vazaoDosBlocos } from './vazao';

export { detalheLigado, useDetalheStore };

/** Parte máxima do orçamento de pixels decodificados que o detalhe pode reservar. */
export const FRACAO_MAX = 0.6;
const FRACAO_MIN = 0.05;
const DEGRAU = 0.05;
/** Teto do nível 1 (bytes dos blocos baixados de uma vez, pelo tamanho medido). */
export const BYTES_MAX = 200e6;
const JANELA_MIN = 2;

/**
 * Parâmetros ajustáveis (medição: `__amaview.detalhe.ajustar({...})`).
 * `basePerto = baseLonge` desliga a base menor de perto; `anel = 0`, a pré-busca.
 */
export const PARAMETROS = {
  /** Base (imagem inteira) quando os blocos cobrem a vista. */
  basePerto: 900 as ImageWidth,
  /** O mesmo, com conexão econômica (os blocos ficam em 3600). */
  basePertoEconomica: 450 as ImageWidth,
  /** Base no resto do tempo. */
  baseLonge: LARGURA_BASE as ImageWidth,
  /**
   * Subir de nível: espera depois de o zoom assentar. Medido (celular, 4G):
   * zoom nítido em 2,0 s com 150 ms, 2,4 s com 250 e 2,6 s com 600, sem nível
   * intermediário desperdiçado.
   */
  subirNivelMs: 150,
  /**
   * Subir de novo no mesmo gesto de zoom (contínuo e lento, com pausas maiores
   * que `subirNivelMs`): espera mais, para não baixar cada nível do caminho.
   * Medido (banda 02, zoom 2 → 6,5 em passos de 200 ms): 3600, 7200 e 14400
   * baixados (32 blocos jogados fora).
   */
  subirDeNovoMs: 600,
  /** Mudanças de zoom com menos que isto entre si são o mesmo gesto. */
  gestoMs: 1000,
  /**
   * Descer de nível: só com a vista parada este tempo. Medido (celular, 4G):
   * afastar custa 0,8 MB e 1,4 s com 1000 ms, 2,3 MB e 2,4 s com 2000.
   */
  descerNivelMs: 1000,
  /** Base para a menor: vista coberta (e parada, sem tocar) por este tempo. */
  baseParaPertoMs: 800,
  /** Base de volta à maior: vista descoberta (e sem tocar) por este tempo. */
  baseParaLongeMs: 2000,
  /** Blocos de pré-busca em volta da vista (0 = sem anel). */
  anel: 1,
  /**
   * Anel também tocando (o do quadro atual e o do próximo). Desligado: como o
   * quadro avança, o anel acaba baixado em TODOS os quadros — medido no z6,
   * 439 blocos (50 MB) contra 68 sem ele.
   */
  anelTocando: false,
  /**
   * Antes de a base da abertura aparecer, os blocos esperam (a base de 0,5 MB
   * disputava a banda com 12 blocos: em 4G o 1º pixel saía em 8,5 s, contra
   * 3,2 s com os blocos depois) — salvo conexão sabidamente rápida
   * (conexao.ts). Teto da espera, se a base demorar.
   */
  esperaBaseMs: 1500,
  /**
   * Reprodução que não fluiria (a rede não baixa o detalhe que falta antes de o
   * loop dar a volta): o nível de baixo de cada quadro vem primeiro e conta
   * para o portão; o pedido chega depois, em segundo plano. Desligado: medido
   * em 4G no z4,5, os bytes do nível de baixo (+12,7 MB) somavam-se aos do
   * pedido na mesma banda e a 1ª volta demorou mais (60–114 s contra 41 s).
   */
  fluir: false,
};

let ressincronizar: (() => void) | null = null;

/** Ajusta os parâmetros e ressincroniza (debug/medição). */
export function ajustarDetalhe(p: Partial<typeof PARAMETROS>): typeof PARAMETROS {
  Object.assign(PARAMETROS, p);
  ressincronizar?.();
  return { ...PARAMETROS };
}

/** Fração do orçamento a reservar para `px` pixels (degraus de 5%). */
export function fracaoReservada(px: number, total: number): number {
  if (!(total > 0) || px <= 0) return 0;
  const f = Math.ceil(px / total / DEGRAU - 1e-9) * DEGRAU;
  return Math.min(FRACAO_MAX, Math.max(FRACAO_MIN, Number(f.toFixed(2))));
}

export interface CamadaDetalhe {
  uid: string;
  produto: string;
  /** Nível DESTA camada (o maior do produto até o nível pedido). */
  nivel: Nivel;
  tempos: (number | null)[];
  faixa: Faixa;
  /** A faixa com o anel de pré-busca (null/ausente = sem anel). */
  anel?: Faixa | null;
}

/**
 * Pedidos ao cache: nível 1 (download) de `horizonte` quadros a partir do
 * atual, nível 2 (decodificado) da `janela` e, por último, o anel em volta da
 * vista dos `quadrosDoAnel` (só download). Puro — testado.
 */
export function montarPedidos(
  camadas: readonly CamadaDetalhe[],
  n: number,
  index: number,
  horizonte: number,
  janela: readonly number[],
  quadrosDoAnel: readonly number[] = [],
): FrameRequest<number>[] {
  const pedidos: FrameRequest<number>[] = [];
  const pedir = (c: CamadaDetalhe, i: number, prioridade: number, decode: number | null, blocos: Iterable<[number, number]>) => {
    const t = c.tempos[i];
    if (t === null || t === undefined) return;
    for (const [l, col] of blocos) {
      pedidos.push({
        url: urlDoBloco(c.produto, t, c.nivel, l, col),
        productId: chaveDoBloco(c.produto, c.nivel, l, col),
        time: t,
        width: c.nivel,
        priority: prioridade,
        decode,
      });
    }
  };
  const h = Math.min(n, horizonte);
  for (const c of camadas) {
    for (let k = 0; k < h; k++) pedir(c, (index + k) % n, k, null, blocosDe(c.faixa));
    janela.forEach((i, p) => pedir(c, i, p, p, blocosDe(c.faixa)));
    if (c.anel) quadrosDoAnel.forEach((i, p) => pedir(c, i, h + p, null, blocosDoAnel(c.faixa, c.anel!)));
  }
  return pedidos;
}

/**
 * Blocos de reserva de um bloco que falhou de vez (404) no nível `nivel`: o
 * que cobre a mesma área no nível de baixo — e, se esse também falhou, o de
 * baixo dele. `status` diz o estado de cada URL no cache. Puro — testado.
 */
export function blocosDeReserva(
  produto: string,
  tempo: number,
  nivel: Nivel,
  linha: number,
  coluna: number,
  niveis: readonly Nivel[],
  status: (url: string) => string | null,
): { nivel: Nivel; linha: number; coluna: number }[] {
  const out: { nivel: Nivel; linha: number; coluna: number }[] = [];
  let n = nivel;
  let l = linha;
  let c = coluna;
  while (status(urlDoBloco(produto, tempo, n, l, c)) === 'error') {
    const abaixo = nivelAbaixo(n, niveis);
    if (!abaixo) break;
    const f = faixaCorrespondente(n, abaixo, l, c);
    n = abaixo;
    l = f.l0;
    c = f.c0;
    out.push({ nivel: n, linha: l, coluna: c });
  }
  return out;
}

/**
 * A posição está pronta: o bloco chegou, ou falhou de vez e a reserva (o nível
 * de baixo) chegou ou também falhou. Sem nível de baixo, a base cobre. Puro.
 */
export function posicaoPronta(
  produto: string,
  tempo: number,
  nivel: Nivel,
  linha: number,
  coluna: number,
  niveis: readonly Nivel[],
  status: (url: string) => string | null,
): boolean {
  const st = status(urlDoBloco(produto, tempo, nivel, linha, coluna));
  if (st === 'ready') return true;
  if (st !== 'error') return false;
  const abaixo = nivelAbaixo(nivel, niveis);
  if (!abaixo) return true;
  const f = faixaCorrespondente(nivel, abaixo, linha, coluna);
  return posicaoPronta(produto, tempo, abaixo, f.l0, f.c0, niveis, status);
}

/** Quadros no nível 1: todos, se couberem em `BYTES_MAX`; senão os próximos (nunca menos que a janela). */
export function horizonteDeDownload(n: number, bytesPorQuadro: number, janela: number): number {
  if (bytesPorQuadro <= 0) return n;
  const cabem = Math.floor(BYTES_MAX / bytesPorQuadro);
  return Math.min(n, Math.max(janela, cabem));
}

/**
 * A reprodução fluiria no nível pedido? Não, se a rede (`vazao`, bytes/s) leva
 * mais para baixar o que falta (`faltam`, bytes) do que o loop leva para dar a
 * volta (`volta`, ms). Sem medida de vazão ainda: flui (não há como saber).
 * Puro — testado.
 */
export function reproducaoFluiria(faltam: number, vazao: number | null, volta: number): boolean {
  if (faltam <= 0 || vazao === null || !(vazao > 0)) return true;
  return (faltam / vazao) * 1000 <= volta;
}

/** Níveis de todas as camadas juntas (o pedido vale para todas). */
export function uniaoDeNiveis(listas: readonly (readonly Nivel[])[]): Nivel[] {
  return [...new Set(listas.flat())].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------

let mapa: OlMap | null = null;
/** Camadas e faixas da última sincronização (gate e exportação usam ESTAS, não a vista ao vivo). */
let sincronizadas: CamadaDetalhe[] = [];

export function camadasSincronizadas(): readonly CamadaDetalhe[] {
  return sincronizadas;
}

/** (debug) camadas do fluxo agora (vazio = reprodução no nível pedido). */
export const camadasDeFluxoAgora = (): readonly CamadaDetalhe[] => deFluxo;

function vistaAtual(): Extent | null {
  const size = mapa?.getSize();
  if (!mapa || !size || !size[0] || !size[1]) return null;
  return mapa.getView().calculateExtent(size);
}

const statusDoBloco = (url: string) => blocoCache.status(url);

/**
 * Camadas no nível de baixo, enquanto a reprodução não fluiria no pedido
 * (`PARAMETROS.fluir`): baixadas primeiro e aceitas pelo portão.
 */
let deFluxo: CamadaDetalhe[] = [];

function camadaPronta(c: CamadaDetalhe, i: number): boolean {
  const t = c.tempos[i];
  if (t === null || t === undefined) return true;
  const niveis = niveisDo(c.produto);
  for (const [l, col] of blocosDe(c.faixa)) {
    if (!posicaoPronta(c.produto, t, c.nivel, l, col, niveis, statusDoBloco)) return false;
  }
  return true;
}

/**
 * Todos os blocos à vista do quadro `i` prontos (ou, falhados de vez, com a
 * reserva pronta)? Com o fluxo, basta o nível de baixo da camada.
 */
export function quadroDetalhePronto(i: number): boolean {
  return sincronizadas.every((c) => {
    if (camadaPronta(c, i)) return true;
    const f = deFluxo.find((d) => d.uid === c.uid);
    return !!f && camadaPronta(f, i);
  });
}

/**
 * Pedidos de reserva: para cada bloco à vista que falhou de vez, nos quadros
 * da `janela`, o de baixo — decodificado logo depois dos do nível pedido.
 */
function pedidosDeReserva(camadas: readonly CamadaDetalhe[], janela: readonly number[]): FrameRequest<number>[] {
  const pedidos: FrameRequest<number>[] = [];
  for (const c of camadas) {
    const niveis = niveisDo(c.produto);
    janela.forEach((i, p) => {
      const t = c.tempos[i];
      if (t === null || t === undefined) return;
      for (const [l, col] of blocosDe(c.faixa)) {
        for (const r of blocosDeReserva(c.produto, t, c.nivel, l, col, niveis, statusDoBloco)) {
          pedidos.push({
            url: urlDoBloco(c.produto, t, r.nivel, r.linha, r.coluna),
            productId: chaveDoBloco(c.produto, r.nivel, r.linha, r.coluna),
            time: t,
            width: r.nivel,
            priority: p + 0.5,
            decode: p + 0.5,
          });
        }
      }
    });
  }
  return pedidos;
}

/** Blocos à vista que falharam de vez na janela (mudou → pedir as reservas). */
function contarFalhas(camadas: readonly CamadaDetalhe[], janela: readonly number[]): number {
  let n = 0;
  for (const c of camadas) {
    for (const i of janela) {
      const t = c.tempos[i];
      if (t === null || t === undefined) continue;
      for (const [l, col] of blocosDe(c.faixa)) if (statusDoBloco(urlDoBloco(c.produto, t, c.nivel, l, col)) === 'error') n++;
    }
  }
  return n;
}

function camadasDoPlano(plan: LoopPlan | null, vista: Extent | null, nivel: Nivel, anel: number): CamadaDetalhe[] {
  if (!vista) return [];
  const out: CamadaDetalhe[] = [];
  for (const l of activePlanLayers(plan)) {
    const nivelDaCamada = nivelDoProduto(nivel, niveisDo(l.productId));
    const faixa = faixaVisivel(vista, imageExtentFor(l.productId, nivelDaCamada), nivelDaCamada);
    if (!faixa) continue;
    out.push({
      uid: l.uid,
      produto: l.productId,
      nivel: nivelDaCamada,
      tempos: l.times,
      faixa,
      anel: anel > 0 ? faixaComAnel(faixa, nivelDaCamada, anel) : null,
    });
  }
  return out;
}

/** As camadas no nível de baixo do pedido (as que têm um acima da base), para o fluxo. */
function camadasDeFluxo(camadas: readonly CamadaDetalhe[], vista: Extent | null): CamadaDetalhe[] {
  if (!vista) return [];
  const out: CamadaDetalhe[] = [];
  for (const c of camadas) {
    const abaixo = nivelAbaixo(c.nivel, niveisDo(c.produto));
    if (!abaixo) continue;
    const faixa = faixaVisivel(vista, imageExtentFor(c.produto, abaixo), abaixo);
    if (faixa) out.push({ uid: c.uid, produto: c.produto, nivel: abaixo, tempos: c.tempos, faixa, anel: null });
  }
  return out;
}

/** Liga o motor com o mapa. Retorna a função de desligar. */
export function iniciarMotorDetalhe(map: OlMap): () => void {
  mapa = map;
  const orcamentoTotal = detectDeviceProfile().budgetPixels;
  const orcamento = orcamentoTotal * FRACAO_MAX;
  let liberar: (() => void) | null = null;
  let reservada = 0;
  const reservar = (px: number) => {
    // Exportando, a nitidez do satélite não muda no meio do vídeo.
    if (useLoopStore.getState().exportLocked) return;
    const f = fracaoReservada(px, orcamentoTotal);
    if (f === reservada) return;
    reservada = f;
    if (f > 0) {
      liberar = reservePixelBudget(f, 'detalhe');
    } else {
      liberar?.();
      liberar = null;
    }
  };

  // Histerese do nível (a mesma do satélite): subir pouco depois de o zoom
  // assentar, descer só com a vista parada — cada toque no zoom que cruza um
  // nível não pode mandar baixar tudo de novo. Teto menor (conexão econômica,
  // qualidade "Alta") ou detalhe desligado: desce na hora.
  let emUso: Nivel | null | undefined;
  let podeSubir = true;
  let podeDescer = true;
  let emTransicao = false;
  // Gesto de zoom em curso (mudanças a menos de `gestoMs`) e se já subiu nele.
  let ultimaMudancaDeZoom = -Infinity;
  let subiuNoGesto = false;
  const posto = (n: Nivel | null) => n ?? 0;
  const comHisterese = (desejado: Nivel | null, teto: number): Nivel | null => {
    const subir = posto(desejado) > posto(emUso ?? null);
    const descer = posto(desejado) < posto(emUso ?? null);
    if (
      emUso === undefined ||
      (desejado === null && !detalheLigado()) ||
      posto(emUso) > teto ||
      // Na transição para a foto de fundo a imagem está sumindo: nenhum nível NOVO
      // (só sair do "sem detalhe", para não ficar só com a base).
      (subir && podeSubir && (!emTransicao || emUso === null)) ||
      (descer && podeDescer)
    ) {
      if (subir && emUso !== undefined) subiuNoGesto = true;
      emUso = desejado;
    }
    return emUso;
  };

  const publicarProgresso = () => {
    let total = 0;
    let baixados = 0;
    let bytes = 0;
    let faltam = 0;
    for (const c of sincronizadas) {
      const estimado = bytesPorBloco(c.produto, c.nivel);
      for (const t of c.tempos) {
        if (t === null) continue;
        for (const [l, col] of blocosDe(c.faixa)) {
          const url = urlDoBloco(c.produto, t, c.nivel, l, col);
          total++;
          if (blocoCache.isDownloaded(url)) {
            baixados++;
            const b = blocoCache.bytes(url);
            if (Number.isFinite(b)) bytes += b;
          } else {
            faltam += estimado;
          }
        }
      }
    }
    const p = useDetalheStore.getState().progresso;
    faltam = Math.round(faltam);
    if (p.baixados !== baixados || p.total !== total || p.bytes !== bytes || p.faltam !== faltam) {
      useDetalheStore.setState({ progresso: { baixados, total, bytes, faltam } });
    }
  };

  // Base (imagem inteira) de perto: menor quando os blocos cobrem a vista
  // inteira. Só troca parada (nunca no meio da reprodução nem exportando) e
  // depois de a condição durar um pouco — ida e volta pelo zoom não manda
  // baixar a base duas vezes.
  let timerBase: ReturnType<typeof setTimeout> | null = null;
  let basePendente: ImageWidth | null = null;
  const alvoDaBase = (produtos: readonly string[], vista: Extent | null, fade: number): ImageWidth | null => {
    if (fade <= 0) return null; // sem GOES na tela: tanto faz, fica como está
    const { quality } = useLoopStore.getState();
    const economica = conexaoEconomica();
    const teto = tetoDoDetalhe(quality, economica);
    const niveis = uniaoDeNiveis(produtos.map((p) => niveisDo(p)));
    const cobre =
      detalheLigado() &&
      !!vista &&
      produtos.length > 0 &&
      nivelPedido(sectorCssPixels(), map.getPixelRatio(), LARGURA_BASE, niveis, teto) !== null &&
      produtos.every((p) => vistaDentroDaImagem(vista, imageExtentFor(p, 7200)));
    if (!cobre) return PARAMETROS.baseLonge;
    return economica ? PARAMETROS.basePertoEconomica : PARAMETROS.basePerto;
  };
  const produtosDoPlano = (plan: LoopPlan | null) => activePlanLayers(plan).map((l) => l.productId);
  const decidirBase = (alvo: ImageWidth | null) => {
    const atual = useDetalheStore.getState().base;
    const { playing, exportLocked } = useLoopStore.getState();
    if (alvo === null || alvo === atual || playing || exportLocked) {
      if (timerBase) clearTimeout(timerBase);
      timerBase = null;
      basePendente = null;
      return;
    }
    // Nenhuma base na tela ainda (abertura): decide já — esperar faria pedir
    // uma base e cancelá-la logo depois.
    if (!usePlanStore.getState().firstFrameShown) {
      if (timerBase) clearTimeout(timerBase);
      timerBase = null;
      basePendente = null;
      useDetalheStore.setState({ base: alvo });
      return;
    }
    if (basePendente === alvo) return;
    if (timerBase) clearTimeout(timerBase);
    basePendente = alvo;
    timerBase = setTimeout(
      () => {
        timerBase = null;
        basePendente = null;
        const { playing: tocando, exportLocked: travado } = useLoopStore.getState();
        const agora = alvoDaBase(produtosDoPlano(usePlanStore.getState().plan), vistaAtual(), goesFade(map.getView().getResolution()));
        if (agora === alvo && !tocando && !travado) useDetalheStore.setState({ base: alvo });
      },
      alvo < atual ? PARAMETROS.baseParaPertoMs : PARAMETROS.baseParaLongeMs,
    );
  };

  // Janela e falhas (404) à vista da última sincronização: uma falha nova
  // manda sincronizar de novo, para pedir a reserva (conferirFalhas).
  let ultimaJanela: readonly number[] = [];
  let falhasVistas = 0;
  // Anel à espera da vista pronta (conferirFalhas o libera).
  let anelPendente = false;
  // Base primeiro: quando os blocos começaram a esperar por ela (0 = não esperam).
  let esperandoBaseDesde = 0;
  let timerEsperaBase: ReturnType<typeof setTimeout> | null = null;

  /** Os blocos esperam a base da abertura? (até `esperaBaseMs`; com conexão rápida, não) */
  const esperarBase = (): boolean => {
    if (usePlanStore.getState().firstFrameShown) return false;
    const c = useConexaoStore.getState();
    if (c.rapida && c.forcar !== true) return false;
    const agora = performance.now();
    if (!esperandoBaseDesde) {
      esperandoBaseDesde = agora;
      timerEsperaBase = setTimeout(() => {
        timerEsperaBase = null;
        sincronizar();
      }, PARAMETROS.esperaBaseMs);
    }
    return agora - esperandoBaseDesde < PARAMETROS.esperaBaseMs;
  };

  const sincronizarAgora = () => {
    const { plan } = usePlanStore.getState();
    const { quality } = useLoopStore.getState();
    const economica = conexaoEconomica();
    const teto = tetoDoDetalhe(quality, economica);
    const fade = goesFade(map.getView().getResolution());
    emTransicao = fade < 1;
    const vista = vistaAtual();
    const niveis = uniaoDeNiveis(activePlanLayers(plan).map((l) => niveisDo(l.productId)));
    const desejado =
      detalheLigado() && plan && plan.frames.length
        ? nivelPedido(sectorCssPixels(), map.getPixelRatio(), currentPlayWidth() ?? LARGURA_BASE, niveis, teto)
        : null;
    const nivel = comHisterese(desejado, teto);
    if (useDetalheStore.getState().nivel !== nivel) useDetalheStore.setState({ nivel });
    decidirBase(alvoDaBase(produtosDoPlano(plan), vista, fade));
    // Sem nível, ou o GOES já sumiu sob a foto de fundo: nenhum bloco.
    if (!nivel || !plan || fade <= 0) {
      sincronizadas = [];
      deFluxo = [];
      ultimaJanela = [];
      falhasVistas = 0;
      anelPendente = false;
      blocoCache.sync([]);
      reservar(0);
      publicarProgresso();
      return;
    }
    const { playing, mode, fps } = useLoopStore.getState();
    const anelPedido = economica || (playing && !PARAMETROS.anelTocando) ? 0 : PARAMETROS.anel;
    const camadas = camadasDoPlano(plan, vista, nivel, anelPedido);
    sincronizadas = camadas;
    const { index } = useTimeStore.getState();
    // Anel só com a vista pronta: antes, ele disputava a banda com ela.
    anelPendente = anelPedido > 0 && !quadroDetalhePronto(index);
    if (anelPendente) for (const c of camadas) c.anel = null;
    const anel = anelPendente ? 0 : anelPedido;
    const n = plan.frames.length;
    let px = 0;
    let bytesPorQuadro = 0;
    for (const c of camadas) {
      for (const [l, col] of blocosDe(c.faixa)) px += pixelsDoBloco(c.nivel, l, col);
      bytesPorQuadro += blocosDaFaixa(c.faixa) * bytesPorBloco(c.produto, c.nivel);
    }
    const lugares = px > 0 ? Math.max(JANELA_MIN, Math.min(MAX_DECODE_WINDOW, Math.floor(orcamento / px))) : MAX_DECODE_WINDOW;
    // Antes do primeiro play (portal parado na medição mais recente): só os
    // blocos do quadro na tela, como o satélite (loop/preloader.ts).
    const todoLoop = loopLoadAllowed();
    const janela = todoLoop ? decodeWindow(index, n, lugares, { playing, mode, direction: getPlaybackDirection() }) : [index];
    // Conexão econômica: adiante, só o que a janela vai mostrar.
    const horizonte = !todoLoop ? 1 : economica ? janela.length : horizonteDeDownload(n, bytesPorQuadro, janela.length);
    // Anel: o quadro na tela (e, com `anelTocando`, o próximo).
    const quadrosDoAnel = anel > 0 ? (playing && n > 1 ? [index, (index + 1) % n] : [index]) : [];
    ultimaJanela = janela;
    falhasVistas = contarFalhas(camadas, janela);

    // Fluxo: tocando, se a rede não baixa o que falta antes de o loop dar a
    // volta, o nível de baixo de cada quadro vem primeiro (¼ dos bytes) e
    // conta para o portão; o pedido segue atrás, e cada quadro fica nítido
    // quando chega (camada.ts desenha o de baixo onde falta o de cima).
    deFluxo = [];
    if (PARAMETROS.fluir && playing && todoLoop && n > 1) {
      let faltam = 0;
      for (const c of camadas) {
        const estimado = bytesPorBloco(c.produto, c.nivel);
        for (let k = 0; k < Math.min(n, horizonte); k++) {
          const t = c.tempos[(index + k) % n];
          if (t === null || t === undefined) continue;
          for (const [l, col] of blocosDe(c.faixa)) if (!blocoCache.isDownloaded(urlDoBloco(c.produto, t, c.nivel, l, col))) faltam += estimado;
        }
      }
      const volta = (n / Math.max(0.5, fps)) * 1000;
      if (!reproducaoFluiria(faltam, vazaoDosBlocos(), volta)) deFluxo = camadasDeFluxo(camadas, vista);
    }
    const pedidos = [...montarPedidos(camadas, n, index, horizonte, janela, quadrosDoAnel), ...pedidosDeReserva(camadas, janela)];
    if (deFluxo.length) {
      // O de baixo primeiro (prioridades 0…n), o pedido depois (n + k).
      for (const p of pedidos) p.priority += n;
      pedidos.unshift(...montarPedidos(deFluxo, n, index, horizonte, janela));
    }
    // Base primeiro: na abertura os blocos esperam a base aparecer (até `esperaBaseMs`).
    blocoCache.sync(esperarBase() ? [] : pedidos);
    // As reservas não entram na conta: cada uma ocupa (no máximo) ¼ do bloco
    // que falhou, e o que falhou não ocupa nada. O fluxo, ¼ do pedido.
    reservar(Math.min(n, lugares) * px * (deFluxo.length ? 1.25 : 1));
    publicarProgresso();
  };

  let agendado = false;
  const sincronizar = () => {
    if (agendado) return;
    agendado = true;
    queueMicrotask(() => {
      agendado = false;
      sincronizarAgora();
    });
  };
  ressincronizar = sincronizar;

  // Um bloco à vista falhou de vez (404): sincroniza para pedir a reserva. A
  // vista ficou pronta com o anel à espera: sincroniza para pedi-lo.
  let falhasAgendadas = false;
  const conferirFalhas = () => {
    if (falhasAgendadas) return;
    falhasAgendadas = true;
    queueMicrotask(() => {
      falhasAgendadas = false;
      if (contarFalhas(sincronizadas, ultimaJanela) !== falhasVistas) sincronizar();
      else if (anelPendente && quadroDetalhePronto(useTimeStore.getState().index)) sincronizar();
    });
  };

  let progressoAgendado: ReturnType<typeof setTimeout> | null = null;
  const agendarProgresso = () => {
    if (progressoAgendado) return;
    progressoAgendado = setTimeout(() => {
      progressoAgendado = null;
      publicarProgresso();
    }, 250);
  };

  let timerSubir: ReturnType<typeof setTimeout> | null = null;
  let timerDescer: ReturnType<typeof setTimeout> | null = null;
  const aoMudarZoom = () => {
    podeSubir = false;
    podeDescer = false;
    if (timerSubir) clearTimeout(timerSubir);
    if (timerDescer) clearTimeout(timerDescer);
    const agora = performance.now();
    if (agora - ultimaMudancaDeZoom > PARAMETROS.gestoMs) subiuNoGesto = false;
    ultimaMudancaDeZoom = agora;
    timerSubir = setTimeout(
      () => {
        podeSubir = true;
        sincronizar();
      },
      subiuNoGesto ? PARAMETROS.subirDeNovoMs : PARAMETROS.subirNivelMs,
    );
    timerDescer = setTimeout(() => {
      podeDescer = true;
      sincronizar();
    }, PARAMETROS.descerNivelMs);
  };
  const aoMover = () => sincronizar();
  map.on('moveend', aoMover);
  map.on('change:size', aoMover);

  // Exportação: o quadro atual só é capturado com o detalhe à vista pronto.
  const esperarDetalhe = () =>
    new Promise<void>((resolve) => {
      const pronto = () => quadroDetalhePronto(useTimeStore.getState().index);
      if (pronto()) return resolve();
      const parar = blocoCache.subscribe(() => {
        if (!pronto()) return;
        parar();
        resolve();
      });
    });

  const unsubs = [
    acompanharConexao(),
    ligarEspelho(),
    registrarBaseDeAbertura((produtos) => decidirBase(alvoDaBase(produtos, vistaAtual(), goesFade(map.getView().getResolution())))),
    subscribeSelector(usePlanStore, (s) => s.firstFrameShown, sincronizar),
    subscribeSelector(usePlanStore, (s) => s.plan, sincronizar),
    subscribeSelector(usePlanStore, (s) => s.playWidth, sincronizar),
    subscribeSelector(useTimeStore, (s) => s.index, sincronizar),
    subscribeSelector(useLoopStore, (s) => `${s.playing}|${s.mode}|${s.exportLocked}|${s.quality}|${s.loopArmed}|${s.fps}`, sincronizar),
    subscribeSelector(useBlocosStore, (s) => s.estado, sincronizar),
    subscribeSelector(useBlocosStore, (s) => s.niveis, sincronizar),
    subscribeSelector(useConexaoStore, (s) => conexaoEconomica(s), sincronizar),
    subscribeSelector(useViewStore, (s) => s.resolution, aoMudarZoom),
    blocoCache.subscribe(agendarProgresso),
    blocoCache.subscribe(conferirFalhas),
    registerFrameGate(quadroDetalhePronto),
    registerFrameReadiness(esperarDetalhe),
  ];
  if (useBlocosStore.getState().estado === 'verificando') void verificarBlocos();
  sincronizar();

  return () => {
    unsubs.forEach((u) => u());
    map.un('moveend', aoMover);
    map.un('change:size', aoMover);
    if (progressoAgendado) clearTimeout(progressoAgendado);
    if (timerSubir) clearTimeout(timerSubir);
    if (timerDescer) clearTimeout(timerDescer);
    if (timerBase) clearTimeout(timerBase);
    if (timerEsperaBase) clearTimeout(timerEsperaBase);
    liberar?.();
    blocoCache.sync([]);
    sincronizadas = [];
    deFluxo = [];
    ressincronizar = null;
    useDetalheStore.setState({ nivel: null, base: LARGURA_BASE, progresso: PROGRESSO_VAZIO });
    mapa = null;
  };
}
